import { useLocalObservable } from 'mobx-react-lite';
import { MouseEvent, useEffect } from 'react';

import Tasks from 'APP/Tasks';
import { MessageViewPageType } from 'APP/main/ChatView/Messages/Messages.types';
import { PayloadType } from 'APP/model/message/messageModel.types';
import Entities from 'STORE';
import { Group } from 'STORE/Groups/Group';
import { ChatMessage } from 'STORE/Messages/Message/ChatMessage/ChatMessage';

import { DISABLE_QUOTE_ON_DOUBLE_CLICK_ATTR_NAME } from './Message.constants';

const mediaPayloadTypes = [
  PayloadType.Image,
  PayloadType.Album,
  PayloadType.Video,
  PayloadType.Article,
];

interface IMessagePresenterParams {
  message: ChatMessage;
  messageViewPageType: MessageViewPageType;
}

interface IMessagePresenter {
  viewTimer: ReturnType<typeof setTimeout> | null;
  isPreviewMessage: boolean;
  isViewMessage: boolean;
  isFocused: boolean;
  hasContextMenu: boolean;
  group: Group | null;
  message: ChatMessage;
  hasAnimation: boolean;
  isReading: boolean;
  payloadType: PayloadType;
  isPreview: boolean;
  isView: boolean;
  hasViewObserver: boolean;
  isMainMessageViewPage: boolean;
  hasPreviewObserver: boolean;
  setMessage(message: ChatMessage): void;
  onFinishAnimation(): void;
  unFocus(): void;
  onView(isView: boolean): void;
  onPreview(isView: boolean): void;
  onMessageDoubleClick(event: MouseEvent<HTMLDivElement>): void;
}

export function useMessagePresenter({
  message,
  messageViewPageType,
}: IMessagePresenterParams): IMessagePresenter {
  const presenter = useLocalObservable<IMessagePresenter>(() => ({
    viewTimer: null,
    isPreviewMessage: false,
    isViewMessage: false,
    isFocused: false,
    message,

    setMessage(message: ChatMessage): void {
      this.message = message;
    },

    get hasContextMenu(): boolean {
      const isUnsupportedMessage = !Object.values(PayloadType).some((payloadType) => {
        return payloadType === presenter.message.payload.payloadType;
      });

      if (isUnsupportedMessage) {
        return false;
      }

      return (
        presenter.message &&
        (!presenter.message.payload.isSystem || presenter.message.payload.isStream)
      );
    },

    get group(): Group | null {
      return Entities.GroupsStore.getGroupById(presenter.message.groupId);
    },

    get hasAnimation(): boolean {
      return presenter.isFocused && presenter.isViewMessage;
    },

    get isReading(): boolean {
      return presenter.message.unread && Entities.App.isFocused && !presenter.group?.withoutMe;
    },

    get payloadType(): PayloadType {
      return presenter.message?.payload?.payloadType;
    },

    get isPreview(): boolean {
      return presenter.group?.isFake || presenter.message?.isFake || presenter.isPreviewMessage;
    },

    get isView(): boolean {
      return presenter.group?.isFake || presenter.message?.isFake || presenter.isViewMessage;
    },

    get hasViewObserver(): boolean {
      return !!(
        presenter.message &&
        presenter.group &&
        !presenter.group.isFake &&
        !presenter.message.isFake
      );
    },

    get isMainMessageViewPage(): boolean {
      return messageViewPageType === MessageViewPageType.Main;
    },

    get hasPreviewObserver(): boolean {
      if (!presenter.hasViewObserver) {
        return false;
      }

      if (mediaPayloadTypes.indexOf(presenter.payloadType) < 0) {
        return false;
      }

      if (presenter.payloadType === PayloadType.Article) {
        return !!presenter.message.payload.firstMediaPayload;
      }

      return true;
    },

    onFinishAnimation(): void {
      presenter.isFocused = false;
      presenter.unFocus();
    },

    unFocus(): void {
      if (presenter.message.isFocused && presenter.isMainMessageViewPage) {
        presenter.message.unfocusMessage();
      }
    },

    onView(isView: boolean): void {
      if (!isView && presenter.isViewMessage) {
        presenter.isFocused = false;
      }
      presenter.isViewMessage = isView;

      if (isView && presenter.isReading && presenter.group) {
        Tasks.messaging.confirmReadMessages({
          groupId: presenter.group.id,
          messageId: presenter.message.id,
        });
      }

      if (isView) {
        presenter.unFocus();
        Tasks.messaging.inViewMessage(presenter.message.groupId, presenter.message.id);
      } else {
        presenter.viewTimer && clearTimeout(presenter.viewTimer);
        Tasks.messaging.outViewMessage(presenter.message.groupId, presenter.message.id);
      }
    },

    onPreview(isView: boolean): void {
      presenter.isPreviewMessage = isView;
    },

    onMessageDoubleClick(event: MouseEvent<HTMLDivElement>): void {
      if (
        presenter.message.canBeReplied &&
        !(event.target as HTMLDivElement).closest(`[${DISABLE_QUOTE_ON_DOUBLE_CLICK_ATTR_NAME}]`)
      ) {
        Tasks.messaging.startReplyMessage(presenter.message);
      }
    },
  }));

  useEffect(() => {
    if (presenter.message.isFocused) {
      presenter.isFocused = true;
    }
  }, [presenter.message.isFocused]);

  useEffect(() => {
    return () => {
      presenter.viewTimer && clearTimeout(presenter.viewTimer);
      Tasks.messaging.outViewMessage(presenter.message.groupId, presenter.message.id);
      presenter.unFocus();
    };
  }, []);

  useEffect(() => {
    presenter.setMessage(message);
  }, [message]);

  return presenter;
}
